/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isNone } from '@ember/utils';
import isMultiLangBranchChild from 'cio/utils/visual-workflow-builder/is-multi-lang-branch-child';
import AccountService from 'cio/services/account';
import ActionModel from 'cio/models/action';
import SettingsService from 'cio/services/settings';
import { DEFAULT_MAX_COLLECTION_COUNT } from 'cio/utils/collections';
import CollectionModel from 'cio/models/collection';
import TemplateModel from 'cio/models/template';
import CampaignModel from 'cio/models/campaign';

interface EditorArgs {
  actionAbstract: {
    action: ActionModel;
    [key: string]: any;
  };
  collections: CollectionModel[];
  isSaving: boolean;
  createSplitTask: () => unknown;
  demoMode: boolean;
  onSelect: (action: ActionModel) => unknown;
  onDeselect: () => unknown;
  hasReadOnlyPermission: boolean;
}

export default class Editor extends Component<EditorArgs> {
  @service account!: AccountService;
  @service settings!: SettingsService;

  get isEssentialsTrial() {
    return Boolean(this.account.current.suspendTrialAt);
  }

  get isMultiLangBranchChildAction() {
    return isMultiLangBranchChild(this.args.actionAbstract.action);
  }

  @computed('args.actionAbstract.action.{id,parentAction.splitActions}')
  get split() {
    let { parentAction, id } = this.args.actionAbstract.action;
    if (isNone(parentAction)) {
      return null;
    }

    return parentAction.get('splitActions').find((split) => split.action.id === id);
  }

  @action
  onEditParentTest() {
    let parentAction = this.args.actionAbstract.action.belongsTo('parentAction').value() as ActionModel | null;

    if (parentAction != null) {
      this.args.onSelect(parentAction);
    }
  }

  get isGatedFeature() {
    return this.account.planFeatures.collections.enabled === false;
  }

  get reachedCollectionLimit() {
    let max = this.settings.environmentSettings.maxCollectionSize ?? DEFAULT_MAX_COLLECTION_COUNT;
    return this.args.collections.length >= max;
  }

  @computed('args.actionAbstract{actionType,action}')
  get showDataCampaignTriggerLoopWarning() {
    let { action, actionType } = this.args.actionAbstract;
    let { id, preconditions } = action;
    let template = action.belongsTo('template').value() as TemplateModel;
    let campaign = action.belongsTo('campaign').value() as CampaignModel;
    let { isWebhookTriggered, webhookUrl, branchTree } = campaign;
    // fail early, before more complex logic
    if (
      !(
        isWebhookTriggered === true &&
        actionType === 'webhook_action' &&
        template?.url === webhookUrl &&
        preconditions.length === 0
      )
    ) {
      return false;
    }
    // need to check campaign tree, and check if there is some branch before loop action
    let idx = branchTree.findIndex((action) => action.id === id);
    // if "idx === -1" it should indicate, that the action is inside a branch, but we can make sure of that
    let hasAnyBranchBeforeCurrentAction = branchTree
      .slice(0, idx === -1 ? branchTree.length : idx)
      .some((item) => item.action.isBranch);
    return hasAnyBranchBeforeCurrentAction === false;
  }
}
